import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Coins, Settings } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CreditPricing() {
  const [pricing, setPricing] = useState([]);
  const [editedPricing, setEditedPricing] = useState({});
  const [signupBonus, setSignupBonus] = useState(10);
  const [creditPriceInr, setCreditPriceInr] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/credits/pricing`, { withCredentials: true });
      setPricing(res.data.pricing);
      setSignupBonus(res.data.signup_bonus);
      setCreditPriceInr(res.data.credit_price_inr || 10);
      // Initialize edited state from fetched data
      const map = {};
      res.data.pricing.forEach(p => { map[p.action_key] = p.credits_cost; });
      setEditedPricing(map);
    } catch (err) {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = pricing.some(p => {
    const edited = editedPricing[p.action_key];
    return edited !== undefined && edited !== p.credits_cost;
  });

  const saveAllPricing = async () => {
    const changed = pricing.filter(p => {
      const edited = editedPricing[p.action_key];
      return edited !== undefined && edited !== p.credits_cost;
    });
    if (changed.length === 0) {
      toast.info("No changes to save");
      return;
    }
    setSaving("all");
    try {
      await Promise.all(
        changed.map(p =>
          axios.put(
            `${API}/api/credits/pricing`,
            { action_key: p.action_key, credits_cost: editedPricing[p.action_key] },
            { withCredentials: true }
          )
        )
      );
      toast.success(`Updated ${changed.length} pricing ${changed.length === 1 ? "item" : "items"}`);
      await fetchPricing();
    } catch (err) {
      toast.error("Failed to update pricing");
    } finally {
      setSaving(null);
    }
  };

  const updateSignupBonus = async () => {
    setSaving("signup_bonus");
    try {
      await axios.put(
        `${API}/api/credits/settings`,
        { signup_bonus: parseFloat(signupBonus) },
        { withCredentials: true }
      );
      toast.success("Signup bonus updated");
    } catch (err) {
      toast.error("Failed to update signup bonus");
    } finally {
      setSaving(null);
    }
  };

  const updateCreditPrice = async () => {
    setSaving("credit_price");
    try {
      await axios.put(
        `${API}/api/credits/settings`,
        { credit_price_inr: parseFloat(creditPriceInr) },
        { withCredentials: true }
      );
      toast.success("Credit price updated");
    } catch (err) {
      toast.error("Failed to update credit price");
    } finally {
      setSaving(null);
    }
  };

  const categories = {
    assisted: "Assisted Video Flow",
    actions: "Per-Action Costs",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const grouped = {};
  pricing.forEach((p) => {
    const cat = p.category || "other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 space-y-8" data-testid="credit-pricing-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Coins className="w-6 h-6" />
          Credit Pricing Configuration
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Set the credit cost for each action on the platform.</p>
      </div>

      {/* Signup Bonus */}
      <div className="rounded-lg border p-4 space-y-3" data-testid="signup-bonus-section">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-semibold">Signup Bonus (Free Credits for New Users)</Label>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="number"
            value={signupBonus}
            onChange={(e) => setSignupBonus(e.target.value)}
            className="w-32"
            min="0"
            data-testid="signup-bonus-input"
          />
          <span className="text-sm text-muted-foreground">credits</span>
          <Button
            size="sm"
            onClick={updateSignupBonus}
            disabled={saving === "signup_bonus"}
            data-testid="save-signup-bonus-btn"
          >
            {saving === "signup_bonus" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Credit Price */}
      <div className="rounded-lg border p-4 space-y-3" data-testid="credit-price-section">
        <div className="flex items-center gap-2">
          <Coins className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-semibold">Price per Credit (INR)</Label>
        </div>
        <p className="text-xs text-muted-foreground">This sets how much users pay per credit during Razorpay top-up.</p>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">₹</span>
          <Input
            type="number"
            value={creditPriceInr}
            onChange={(e) => setCreditPriceInr(e.target.value)}
            className="w-32"
            min="1"
            step="1"
            data-testid="credit-price-input"
          />
          <span className="text-sm text-muted-foreground">per credit</span>
          <Button
            size="sm"
            onClick={updateCreditPrice}
            disabled={saving === "credit_price"}
            data-testid="save-credit-price-btn"
          >
            {saving === "credit_price" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      {/* Pricing by Category */}
      {Object.entries(categories).map(([catKey, catLabel]) => {
        const items = grouped[catKey] || [];
        if (items.length === 0) return null;
        return (
          <div key={catKey} className="space-y-3">
            <h2 className="text-base font-semibold border-b pb-2">{catLabel}</h2>
            {items.map((item) => {
              const isChanged = editedPricing[item.action_key] !== undefined && editedPricing[item.action_key] !== item.credits_cost;
              return (
                <div
                  key={item.action_key}
                  className={`flex items-center justify-between p-3 rounded-lg border bg-card ${isChanged ? "border-amber-400 bg-amber-50/50" : ""}`}
                  data-testid={`pricing-row-${item.action_key}`}
                >
                  <div>
                    <p className="text-sm font-medium">{item.label}</p>
                    <p className="text-xs text-muted-foreground">{item.action_key}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={editedPricing[item.action_key] ?? item.credits_cost}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) {
                          setEditedPricing(prev => ({ ...prev, [item.action_key]: val }));
                        }
                      }}
                      className="w-24 text-center"
                      min="0"
                      step="0.5"
                      data-testid={`pricing-input-${item.action_key}`}
                    />
                    <span className="text-xs text-muted-foreground w-12">credits</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {/* Save All Button */}
      <div className="sticky bottom-4 pt-4">
        <Button
          onClick={saveAllPricing}
          disabled={saving === "all" || !hasChanges}
          className="w-full h-12 text-base"
          data-testid="save-all-pricing-btn"
        >
          {saving === "all" ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving === "all" ? "Saving..." : hasChanges ? "Save All Changes" : "No Changes"}
        </Button>
      </div>
    </div>
  );
}
